import { Button } from "antd";
import { RefreshCw } from "lucide-react";
import SectionPanel from "pages/analytics/components/SectionPanel";
import {
  EmptyMessage,
  ErrorBanner,
  TableSkeleton,
} from "pages/analytics/components/AnalyticsStates";
import { formatDate } from "pages/analytics/components/analyticsFormatters";

const STATUS_BADGE = {
  idle: "bg-gray-100 text-gray-700",
  running: "bg-blue-100 text-blue-700",
  completed: "bg-green-100 text-green-700",
  failed: "bg-red-100 text-red-700",
};

const EVENT_LABELS = {
  sync_started: "Sync started",
  sync_completed: "Sync completed",
  sync_failed: "Sync failed",
  sync_skipped: "Sync skipped",
};

const formatDateTime = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

export default function Ga4StatusPanel({
  status,
  isLoading,
  isSyncing,
  syncError,
  syncSuccess,
  onSync,
}) {
  const checkpoint = status?.checkpoint;
  const syncStatus = checkpoint?.status ?? "idle";

  const syncButton = (
    <Button
      type="primary"
      size="small"
      icon={<RefreshCw className={`h-3.5 w-3.5 ${isSyncing ? "animate-spin" : ""}`} />}
      loading={isSyncing}
      disabled={isSyncing || isLoading}
      onClick={onSync}
    >
      Manual Sync
    </Button>
  );

  return (
    <SectionPanel title="Sync Status" action={syncButton}>
      {syncSuccess && (
        <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          {syncSuccess}
        </div>
      )}

      {!isLoading && status && !status.configured && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {status.message || "No GA4 credentials configured"}
        </div>
      )}

      {syncError && (
        <div className="mb-4">
          <ErrorBanner message={syncError} />
        </div>
      )}

      {isLoading ? (
        <TableSkeleton rows={4} cols={2} />
      ) : !status ? (
        <EmptyMessage message="Unable to load GA4 status." />
      ) : (
        <div className="space-y-6">
          <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Configuration
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {status.configured
                  ? "Configured"
                  : status.message || "No GA4 credentials configured"}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Property ID
              </dt>
              <dd className="mt-1 text-sm text-gray-900 font-mono">
                {status.propertyId || "-"}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Sync Status
              </dt>
              <dd className="mt-1">
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium capitalize ${STATUS_BADGE[syncStatus] ?? STATUS_BADGE.idle}`}
                >
                  {syncStatus}
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Last Synced Date
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {formatDate(checkpoint?.lastSyncedDate)}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Last Sync Completed
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {formatDateTime(checkpoint?.lastSyncCompletedAt)}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Total Synced Days
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {checkpoint?.totalSyncedDays ?? 0}
              </dd>
            </div>
          </dl>

          {checkpoint?.lastError && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {checkpoint.lastError}
            </div>
          )}

          <div>
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
              Recent Events
            </h4>
            {status.recentEvents?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-xs text-gray-500 uppercase">
                      <th className="pb-2 pr-4 font-medium">Event</th>
                      <th className="pb-2 pr-4 font-medium">Message</th>
                      <th className="pb-2 font-medium">Time</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {status.recentEvents.map((event) => (
                      <tr key={event._id ?? `${event.eventType}-${event.createdAt}`}>
                        <td className="py-2 pr-4 text-gray-900 whitespace-nowrap">
                          {EVENT_LABELS[event.eventType] ?? event.eventType}
                        </td>
                        <td className="py-2 pr-4 text-gray-600">
                          {event.message}
                        </td>
                        <td className="py-2 text-gray-500 whitespace-nowrap">
                          {formatDateTime(event.createdAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyMessage message="No sync events recorded yet." />
            )}
          </div>
        </div>
      )}
    </SectionPanel>
  );
}
